// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { ChargeSortProperty, chargeListingSchema } from '@/billing/api/types';
import { ChargeStatus } from '@/constants/charge-status';
import { ChargeTriggerType } from '@/constants/charge-trigger-type';
import { Sort } from '@/constants/sort';
import { NOT_SET_SENTINEL } from '@/customer/constants/not-set-sentinel';
import { z } from 'zod';

/**
 * The name the charge list sorts by on the wire, for each sort the app offers.
 */
const SORT_NAME_BY_PROPERTY: Record<ChargeSortProperty, string> = {
  project: 'project',
  customer: 'customer',
  label: 'label',
  amount: 'totalPrice',
  servicePeriodStart: 'servicePeriodStart',
  billDate: 'expectedBillableDate',
  status: 'status',
  item: 'item',
};

const chargesResponseSchema = z.object({
  data: chargeListingSchema,
  metadata: responseMetadataSchema,
});

export interface ChargeFilter {
  pageSize: number;
  pageNumber?: number;
  sort?: Sort<ChargeSortProperty>;
  search?: string;
  status?: ChargeStatus;
  // May contain the "Not set" sentinel alongside real customer keys. The
  // sentinel is sent as the `includeNoCustomer` flag, not as a key.
  customerKeys?: string[];
  projectKeys?: string[];
  // Multi-select trigger-type filter. Empty / undefined passes everything
  // through; a populated array restricts results to rows whose trigger
  // type is in the set.
  triggerTypes?: ChargeTriggerType[];
  billDateStart?: string;
  billDateEnd?: string;
  amountMin?: number;
  amountMax?: number;
}

/**
 * Adds the customer and project filters, sending "Not set" as its own flag.
 */
function appendCustomerAndProjectFilters(
  params: URLSearchParams,
  filter: ChargeFilter
) {
  const customerKeys = filter.customerKeys ?? [];
  [...customerKeys]
    .filter((key) => key !== NOT_SET_SENTINEL)
    .sort()
    .forEach((key) => {
      params.append('customers', key);
    });

  if (customerKeys.includes(NOT_SET_SENTINEL)) {
    params.append('includeNoCustomer', 'true');
  }

  [...(filter.projectKeys ?? [])].sort().forEach((key) => {
    params.append('projects', key);
  });
}

// Serializes only the filters that affect the status summary cards:
// customer, project, bill date, and amount range. Produces a stable cache
// key that does not change when status, search, sort, or page change.
export function serializeChargeSummaryScope(filter?: ChargeFilter): string {
  if (!filter) {
    return '';
  }
  const params = new URLSearchParams();
  appendCustomerAndProjectFilters(params, filter);
  if (filter.triggerTypes && filter.triggerTypes.length > 0) {
    [...filter.triggerTypes].sort().forEach((t) => {
      params.append('triggerTypes', t);
    });
  }
  if (filter.billDateStart) {
    params.append('billDateStart', filter.billDateStart);
  }
  if (filter.billDateEnd) {
    params.append('billDateEnd', filter.billDateEnd);
  }
  if (filter.amountMin !== undefined) {
    params.append('amountMin', filter.amountMin.toString());
  }
  if (filter.amountMax !== undefined) {
    params.append('amountMax', filter.amountMax.toString());
  }
  return params.toString();
}

export function serializeChargeFilter(filter?: ChargeFilter): string {
  if (!filter) {
    return '';
  }

  const params = new URLSearchParams();

  params.append('limit', filter.pageSize.toString());
  if (filter.pageNumber !== undefined) {
    params.append('page', filter.pageNumber.toString());
  }
  if (filter.sort) {
    params.append('sortBy', SORT_NAME_BY_PROPERTY[filter.sort.property]);
    params.append('sortOrder', filter.sort.direction);
  }
  if (filter.search) {
    params.append('search', filter.search);
  }
  if (filter.status) {
    params.append('status', filter.status);
  }
  appendCustomerAndProjectFilters(params, filter);
  if (filter.triggerTypes && filter.triggerTypes.length > 0) {
    [...filter.triggerTypes].sort().forEach((t) => {
      params.append('triggerTypes', t);
    });
  }
  if (filter.billDateStart) {
    params.append('billDateStart', filter.billDateStart);
  }
  if (filter.billDateEnd) {
    params.append('billDateEnd', filter.billDateEnd);
  }
  if (filter.amountMin !== undefined) {
    params.append('amountMin', filter.amountMin.toString());
  }
  if (filter.amountMax !== undefined) {
    params.append('amountMax', filter.amountMax.toString());
  }

  return params.toString();
}

export async function getCharges(filter: ChargeFilter = { pageSize: 20 }) {
  const serializedFilter = serializeChargeFilter(filter);
  const url = serializedFilter
    ? `/v1/charge?${serializedFilter}`
    : '/v1/charge';
  const response = await api.get(url);

  const result = await parseResponse(chargesResponseSchema, response.data, {
    schemaName: 'Charges',
    endpoint: url,
  });

  return result.data;
}
